import { camelize } from 'helpers/syntaxHelper';
import { BudgetPhases } from 'business/constants';
import {
    SPLIT_BY,
    BUDGET_LINE_ID,
    NO_SERVICE,
    NO_GESTIONNAIRE,
} from 'routes/BudgetModule/config/constants';
import { translateBlockValue } from 'i18n';
import _ from 'lodash';
import { StatusesSettingsT } from 'routes/BudgetModule/config/types';
import { isInscriptionVoted } from 'routes/BudgetModule/helpers/statusHelpers';
import {
    getCommentForPhase,
    getStatusFieldForPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import {
    ExpenseIncomeImputationDataT,
    ExpenseIncomeImputationsT,
} from '../hooks/expenseIncomeImputations';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';

/**
 * Check if we are in a 'NO SERVICE/GESTIONNAIRE' case
 * without a resource software id
 */
const isNoResourceSelected = (
    requestName: string,
    resourceSoftwareId: string,
): boolean => {
    return (
        [NO_SERVICE, NO_GESTIONNAIRE].includes(requestName) &&
        resourceSoftwareId === ''
    );
};

export const sortInscriptionsVotedFirst =
    (statusesSettings: StatusesSettingsT) =>
    (inscription1: InscriptionT, inscription2: InscriptionT): number => {
        const inscription1IsVoted = isInscriptionVoted(
            inscription1,
            BudgetPhases.SUPPLEMENTARY_BUDGET,
            statusesSettings,
        );
        const inscription2IsVoted = isInscriptionVoted(
            inscription2,
            BudgetPhases.SUPPLEMENTARY_BUDGET,
            statusesSettings,
        );

        if (inscription1IsVoted && !inscription2IsVoted) {
            return -1;
        }
        if (!inscription1IsVoted && inscription2IsVoted) {
            return 1;
        }
        return 0;
    };

const isFilterInInscriptionComment = (
    inscription: InscriptionT,
    filterInscriptions: string,
    phase: BudgetPhases,
): boolean => {
    const phaseComment = getCommentForPhase(phase);
    if (!phaseComment) return false;
    return (
        typeof inscription[phaseComment] === 'string' &&
        inscription[phaseComment]
            .toLowerCase()
            .includes(filterInscriptions.toLowerCase())
    );
};

const isFilterInInscriptionStatus = (
    inscription: InscriptionT,
    filterInscriptions: string,
    phase: BudgetPhases,
): boolean => {
    const statusField = getStatusFieldForPhase(phase);
    // WARNING here using traductions from backend, don't forget to update
    // also 'blocks_manty_values.json' when updating budgetProposal statuses traductions
    if (!statusField) return false;
    const statusValue = translateBlockValue(inscription[statusField]);
    return statusValue.toLowerCase().includes(filterInscriptions.toLowerCase());
};

const isFilterInInscriptionProjects = (
    inscription: InscriptionT,
    filterInscriptions: string,
    projects: Project[],
): boolean => {
    const inscriptionProjects = projects.filter((project) =>
        inscription['project.id'].includes(project.id),
    );
    return inscriptionProjects
        .map((p) => p.name.toLowerCase())
        .some((name) => name.includes(filterInscriptions.toLowerCase()));
};

export const applyInscriptionsFilter = (
    inscriptions: InscriptionT[],
    filterInscriptions: string,
    phase: BudgetPhases,
    projects: Project[],
): InscriptionT[] =>
    inscriptions.filter(
        (inscription) =>
            isFilterInInscriptionComment(
                inscription,
                filterInscriptions,
                phase,
            ) ||
            isFilterInInscriptionStatus(
                inscription,
                filterInscriptions,
                phase,
            ) ||
            isFilterInInscriptionProjects(
                inscription,
                filterInscriptions,
                projects,
            ),
    );

/**
 * Sort pivotedData.data and camelize pivotedData.columns keys
 */
export const getFormattedPivotedData = (
    pivotedDataResponse: ExpenseIncomeImputationsT | undefined,
    exercice: number,
): {
    pivotedData: ExpenseIncomeImputationDataT[];
    columnsDetails: ResponseColumnT[];
} => {
    const pivotedData = pivotedDataResponse
        ? _.sortBy(
              pivotedDataResponse.data,
              `${BUDGET_LINE_ID}${SPLIT_BY}${exercice}`,
          )
        : [];

    const columnsDetails = pivotedDataResponse
        ? pivotedDataResponse.columns.map((column) => ({
              ...column,
              key: camelize(column.key),
          }))
        : [];
    return { pivotedData, columnsDetails };
};

export const getResourceName = (
    resourceName: string,
    resourceSoftwareId: string,
): string | null => {
    const isEmptyResourceSelected = isNoResourceSelected(
        resourceName,
        resourceSoftwareId,
    );
    return isEmptyResourceSelected ? null : resourceName;
};
